import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { funnelsApi } from '../services/funnelsApi';
import type {
  CreateFunnelStepRequest,
  UpdateFunnelStepRequest,
  FunnelStepQuery,
  ReorderFunnelStepsRequest
} from '@troia-v3/types';
import { toast } from 'sonner';

/**
 * Query Keys for Funnel Steps
 */
export const FUNNEL_STEP_KEYS = {
  all: ['funnel-steps'] as const,
  lists: () => [...FUNNEL_STEP_KEYS.all, 'list'] as const,
  list: (query?: FunnelStepQuery) => [...FUNNEL_STEP_KEYS.lists(), query] as const,
  details: () => [...FUNNEL_STEP_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...FUNNEL_STEP_KEYS.details(), id] as const,
  byFunnel: (funnelId: string) => [...FUNNEL_STEP_KEYS.all, 'by-funnel', funnelId] as const,
};

/**
 * Hook to fetch all funnel steps with pagination
 */
export const useFunnelSteps = (query?: FunnelStepQuery) => {
  return useQuery({
    queryKey: FUNNEL_STEP_KEYS.list(query),
    queryFn: () => funnelsApi.getFunnelSteps(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch funnel step by ID
 */
export const useFunnelStep = (id: string | undefined) => {
  return useQuery({
    queryKey: FUNNEL_STEP_KEYS.detail(id!),
    queryFn: () => funnelsApi.getFunnelStepById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch steps for specific funnel
 */
export const useStepsByFunnelId = (funnelId: string | undefined) => {
  return useQuery({
    queryKey: FUNNEL_STEP_KEYS.byFunnel(funnelId!),
    queryFn: () => funnelsApi.getStepsByFunnelId(funnelId!),
    enabled: !!funnelId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create funnel step
 */
export const useCreateFunnelStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFunnelStepRequest) => funnelsApi.createFunnelStep(data),
    onSuccess: (newStep) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.byFunnel(newStep.funnelId) });
      toast.success('Etapa criada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar etapa');
    },
  });
};

/**
 * Hook to update funnel step
 */
export const useUpdateFunnelStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFunnelStepRequest }) =>
      funnelsApi.updateFunnelStep(id, data),
    onSuccess: (updatedStep, variables) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.byFunnel(updatedStep.funnelId) });
      toast.success('Etapa atualizada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar etapa');
    },
  });
};

/**
 * Hook to delete funnel step
 */
export const useDeleteFunnelStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => funnelsApi.deleteFunnelStep(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      toast.success('Etapa excluída com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir etapa');
    },
  });
};

/**
 * Hook to delete funnel step with validation
 */
export const useDeleteFunnelStepWithValidation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => funnelsApi.deleteFunnelStepWithValidation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      toast.success('Etapa excluída com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir etapa');
    },
  });
};

/**
 * Hook to validate funnel step deletion
 */
export const useValidateDeleteFunnelStep = (id: string | undefined) => {
  return useQuery({
    queryKey: [...FUNNEL_STEP_KEYS.detail(id!), 'validate-delete'],
    queryFn: () => funnelsApi.validateDeleteFunnelStep(id!),
    enabled: !!id,
    staleTime: 0, // Always fresh for validation
  });
};

/**
 * Hook to reorder funnel steps
 */
export const useReorderFunnelSteps = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderFunnelStepsRequest) => funnelsApi.reorderFunnelSteps(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      toast.success('Etapas reordenadas com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao reordenar etapas');
    },
  });
};

/**
 * Hook to activate funnel step
 */
export const useActivateFunnelStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => funnelsApi.activateFunnelStep(id),
    onSuccess: (updatedStep) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.detail(updatedStep.id) });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.byFunnel(updatedStep.funnelId) });
      toast.success('Etapa ativada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao ativar etapa');
    },
  });
};

/**
 * Hook to deactivate funnel step
 */
export const useDeactivateFunnelStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => funnelsApi.deactivateFunnelStep(id),
    onSuccess: (updatedStep) => {
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.all });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.detail(updatedStep.id) });
      queryClient.invalidateQueries({ queryKey: FUNNEL_STEP_KEYS.byFunnel(updatedStep.funnelId) });
      toast.success('Etapa desativada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar etapa');
    },
  });
};
